<template>
  <div class="nip-widget">
    <div class="nip-header">
      <span class="nip-header-title">
        <i class="fa fa-football" aria-hidden="true"></i>
        NFL Intelligence
      </span>
      <router-link class="nip-header-link" to="/nfl">Open NFL Center <span aria-hidden="true">&rarr;</span></router-link>
    </div>
    <p class="nip-tagline">Games, matchup context, Power Rankings &amp; live game intelligence</p>

    <!-- Loading -->
    <div v-if="loading" class="nip-skeleton"></div>

    <!-- Quiet failure: the news page stays fully usable -->
    <p v-else-if="error" class="nip-quiet-error">NFL data is unavailable right now.</p>

    <template v-else-if="loaded">
      <p v-if="weekLine" class="nip-week">{{ weekLine }}</p>

      <!-- Live games first -->
      <div v-if="liveGames.length" class="nip-section">
        <span class="nip-section-label">
          <span class="nip-live-dot" aria-hidden="true"></span> Live now
        </span>
        <router-link
          v-for="g in liveGames"
          :key="g.gameId"
          class="nip-game"
          :to="`/nfl/games/${g.gameId}`"
        >
          <span class="nip-game-teams">
            <span class="nip-game-line">
              <NflTeamLogo :team="g.awayTeam" :size="16" />
              <span class="nip-abbr">{{ abbr(g.awayTeam) }}</span>
              <span class="nip-score">{{ scoreText(g.awayScore) }}</span>
            </span>
            <span class="nip-game-line">
              <NflTeamLogo :team="g.homeTeam" :size="16" />
              <span class="nip-abbr">{{ abbr(g.homeTeam) }}</span>
              <span class="nip-score">{{ scoreText(g.homeScore) }}</span>
            </span>
          </span>
          <span class="nip-game-meta">
            <span class="nip-live-tag">LIVE</span>
            <span v-if="g.status" class="nip-game-status">{{ g.status }}</span>
          </span>
        </router-link>
      </div>

      <!-- Upcoming -->
      <div v-if="upcomingGames.length" class="nip-section">
        <span class="nip-section-label">Upcoming</span>
        <router-link
          v-for="g in upcomingGames"
          :key="g.gameId"
          class="nip-game"
          :to="`/nfl/games/${g.gameId}`"
        >
          <span class="nip-game-teams">
            <span class="nip-game-line">
              <NflTeamLogo :team="g.awayTeam" :size="16" />
              <span class="nip-abbr">{{ abbr(g.awayTeam) }}</span>
              <span class="nip-at">@</span>
              <NflTeamLogo :team="g.homeTeam" :size="16" />
              <span class="nip-abbr">{{ abbr(g.homeTeam) }}</span>
              <span v-if="recordPair(g)" class="nip-records">{{ recordPair(g) }}</span>
            </span>
          </span>
          <span class="nip-game-meta">
            <span class="nip-kickoff">{{ kickoff(g) }}</span>
          </span>
        </router-link>
      </div>

      <!-- Intelligence highlight (backend-generated interpretation only) -->
      <div v-if="highlight" class="nip-highlight">
        <span class="nip-kicker">Barbershoptalk Intelligence</span>
        <p class="nip-highlight-headline">{{ highlight.headline }}</p>
        <p v-if="highlightFact" class="nip-highlight-fact">{{ highlightFact }}</p>
        <router-link v-if="highlightGameId" class="nip-highlight-link" :to="`/nfl/games/${highlightGameId}`">
          View matchup <span aria-hidden="true">&rarr;</span>
        </router-link>
      </div>

      <!-- Power Rankings top 3 -->
      <div v-if="powerTop.length" class="nip-section">
        <span class="nip-section-label">Power Rankings</span>
        <router-link
          v-for="p in powerTop"
          :key="p.rank"
          class="nip-pr-row"
          :to="p.team && p.team.teamId ? `/nfl/teams/${p.team.teamId}` : '/nfl/power-rankings'"
        >
          <span class="nip-pr-rank">#{{ p.rank }}</span>
          <NflTeamLogo :team="p.team" :size="18" />
          <span class="nip-pr-abbr">{{ abbr(p.team) }}</span>
          <span class="nip-pr-score">{{ scoreValue(p.powerScore) }}</span>
          <span class="nip-pr-move" :class="`nip-pr-move--${movement(p).kind}`">
            <span aria-hidden="true">{{ movement(p).label }}</span>
            <span class="nip-sr-only">{{ movement(p).srText }}</span>
          </span>
        </router-link>
        <router-link class="nip-pr-cta" to="/nfl/power-rankings">
          View all 32 <span aria-hidden="true">&rarr;</span>
        </router-link>
      </div>

      <p v-if="isEmpty" class="nip-quiet-error">No NFL games or rankings to show yet.</p>
    </template>
  </div>
</template>

<script>
import NflTeamLogo from '@/components/nfl/NflTeamLogo.vue';
import NflSportsService from '@/services/NflSportsService';
import {
  DASH, fmtNum, fmtLocalDate, fmtLocalTime, weekLabel, movementInfo,
  liveGamesFromOverview, upcomingGamesFromOverview,
} from '@/utils/nflFormat';

const POLL_MS = 30000; // matches backend live cache freshness; never poll faster

/**
 * Compact NFL intelligence entry point rendered on the /sports/nfl news feed
 * (mirrors the UfcScores acquisition-surface role for /sports/mma). Overview
 * is fetched on mount and silently refreshed while games are live or still
 * upcoming so a kickoff can move into Live. On failure it degrades to a
 * single quiet line so the news page is never impaired.
 */
export default {
  name: 'NflIntelligencePreview',
  components: { NflTeamLogo },
  data() {
    return {
      loading: false,
      loaded: false,
      error: false,
      overview: null,
      pollTimer: null,
    };
  },
  computed: {
    weekLine() {
      const o = this.overview || {};
      const parts = [];
      if (o.season) parts.push(String(o.season));
      if (o.currentWeek != null) parts.push(weekLabel(o.seasonType, o.currentWeek));
      return parts.join(' · ');
    },
    liveGames() {
      return liveGamesFromOverview(this.overview, { limit: 4 });
    },
    upcomingGames() {
      const liveIds = new Set(this.liveGames.map(g => g.gameId));
      const pool = upcomingGamesFromOverview(this.overview, liveIds);
      // Live games already carry the section; keep the widget compact.
      return pool.slice(0, this.liveGames.length ? 2 : 4);
    },
    shouldPoll() {
      return this.loaded && !this.error && (this.liveGames.length > 0 || this.upcomingGames.length > 0);
    },
    highlight() {
      const list = (this.overview && this.overview.weeklyHighlights) || [];
      return list.length ? list[0] : null;
    },
    highlightFact() {
      const facts = this.highlight && this.highlight.supportingFacts;
      return Array.isArray(facts) && facts.length ? facts[0] : null;
    },
    /**
     * Insights carry team ids, not game ids — match the highlight to a game
     * already present in this same overview payload (selection among backend
     * facts, no new relationships invented).
     */
    highlightGameId() {
      const h = this.highlight;
      if (!h || h.subjectTeamId == null) return null;
      const ids = [h.subjectTeamId, h.comparisonTeamId].filter(v => v != null);
      const games = [
        ...((this.overview && this.overview.liveGames) || []),
        ...((this.overview && this.overview.upcomingGames) || []),
      ];
      const match = games.find(g => {
        const teams = [g.awayTeam && g.awayTeam.teamId, g.homeTeam && g.homeTeam.teamId];
        return ids.every(id => teams.includes(id)) || (ids.length === 1 && teams.includes(ids[0]));
      });
      return match ? match.gameId : null;
    },
    powerTop() {
      return ((this.overview && this.overview.powerRankingsPreview) || []).slice(0, 3);
    },
    isEmpty() {
      return !this.liveGames.length && !this.upcomingGames.length
        && !this.highlight && !this.powerTop.length;
    },
  },
  watch: {
    shouldPoll: {
      immediate: true,
      handler(shouldPoll) {
        if (shouldPoll) this.startPolling();
        else this.stopPolling();
      },
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        if (this.shouldPoll) this.load(true);
      }, POLL_MS);
    },
    stopPolling() {
      if (!this.pollTimer) return;
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    async load(silent = false) {
      if (!silent) {
        this.loading = true;
        this.error = false;
      }
      try {
        const response = await NflSportsService.getOverview();
        this.overview = response.data || {};
        this.loaded = true;
        this.error = false;
      } catch {
        if (!silent) this.error = true;
      } finally {
        if (!silent) this.loading = false;
      }
    },
    abbr(team) {
      return (team && team.abbreviation) || DASH;
    },
    scoreText(score) {
      return score != null ? score : DASH;
    },
    scoreValue(v) {
      return v != null ? fmtNum(v, 1) : DASH;
    },
    recordPair(g) {
      if (!g.awayRecord && !g.homeRecord) return null;
      return `${g.awayRecord || DASH} · ${g.homeRecord || DASH}`;
    },
    kickoff(g) {
      if (!g.kickoffUtc) return g.status || DASH;
      return `${fmtLocalDate(g.kickoffUtc)} · ${fmtLocalTime(g.kickoffUtc)}`;
    },
    movement(p) {
      return movementInfo(p.rankChange);
    },
  },
};
</script>

<style scoped>
.nip-widget {
  background: #ffffff;
  border: 1px solid #d8dee8;
  border-radius: 14px;
  padding: 0 0 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: 'Titillium Web', sans-serif;
  overflow: hidden;
}

.nip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: linear-gradient(155deg, #0c1220 0%, #162033 100%);
  padding: 12px 16px;
}
.nip-header-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  color: #f4efe6;
}
.nip-header-title i { color: #e8b086; }
.nip-header-link {
  font-size: 11.5px;
  font-weight: 800;
  color: #e8b086;
  text-decoration: none;
  white-space: nowrap;
}
.nip-header-link:hover { text-decoration: underline; }
.nip-header-link:focus-visible { outline: 2px solid #e8b086; outline-offset: 2px; }

.nip-tagline {
  margin: 0;
  padding: 0 16px;
  font-size: 11.5px;
  color: #64748b;
}

.nip-skeleton {
  height: 110px;
  margin: 0 16px;
  background: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  border-radius: 10px;
  animation: nip-shimmer 1.5s infinite;
}
@keyframes nip-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .nip-skeleton { animation: none; }
}

.nip-quiet-error {
  margin: 0 16px;
  font-size: 12px;
  color: #64748b;
}

.nip-week {
  margin: 0 16px;
  font-size: 12px;
  font-weight: 800;
  color: #111827;
}

.nip-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 16px;
}
.nip-section-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #64748b;
  margin-bottom: 2px;
}
.nip-live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #dc2626;
  animation: nip-pulse 1.6s infinite;
}
@keyframes nip-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
@media (prefers-reduced-motion: reduce) {
  .nip-live-dot { animation: none; }
}

.nip-game {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 8px;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  text-decoration: none;
  transition: border-color 0.15s;
}
.nip-game:hover { border-color: #9a3412; }
.nip-game:focus-visible { outline: 2px solid #9a3412; outline-offset: 2px; }
.nip-game-teams {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.nip-game-line {
  display: flex;
  align-items: center;
  gap: 6px;
}
.nip-at {
  font-size: 11px;
  font-weight: 800;
  color: #64748b;
}
.nip-abbr {
  font-size: 12px;
  font-weight: 800;
  color: #111827;
}
.nip-score {
  font-size: 12.5px;
  font-weight: 800;
  color: #334155;
  font-variant-numeric: tabular-nums;
}
.nip-records {
  font-size: 11px;
  color: #64748b;
}
.nip-game-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
  text-align: right;
}
.nip-live-tag {
  font-size: 9.5px;
  font-weight: 900;
  letter-spacing: 1px;
  color: #b91c1c;
}
.nip-game-status {
  font-size: 11px;
  font-weight: 700;
  color: #334155;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.nip-kickoff {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
}

.nip-highlight {
  margin: 0 16px;
  background: #f7f1ea;
  border-left: 3px solid #9a3412;
  border-radius: 0 10px 10px 0;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.nip-kicker {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.1px;
  color: #7c2d12;
}
.nip-kicker::before {
  content: '';
  width: 7px;
  height: 10px;
  flex-shrink: 0;
  background-image:
    linear-gradient(#9a3412, #9a3412),
    linear-gradient(#9a3412, #9a3412);
  background-size: 2px 10px, 2px 7px;
  background-position: left center, right center;
  background-repeat: no-repeat;
}
.nip-highlight-headline {
  margin: 0;
  font-size: 13px;
  font-weight: 800;
  color: #111827;
  line-height: 1.35;
}
.nip-highlight-fact {
  margin: 0;
  font-size: 11.5px;
  color: #334155;
  line-height: 1.4;
}
.nip-highlight-link {
  align-self: flex-start;
  font-size: 11.5px;
  font-weight: 800;
  color: #7c2d12;
  text-decoration: none;
}
.nip-highlight-link:hover { text-decoration: underline; }
.nip-highlight-link:focus-visible { outline: 2px solid #9a3412; outline-offset: 2px; }

.nip-pr-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 8px;
  text-decoration: none;
}
.nip-pr-row:hover { background: #f3f5f9; }
.nip-pr-row:focus-visible { outline: 2px solid #9a3412; outline-offset: -2px; }
.nip-pr-rank {
  font-size: 12px;
  font-weight: 900;
  color: #7c2d12;
  width: 24px;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}
.nip-pr-abbr {
  font-size: 12.5px;
  font-weight: 800;
  color: #111827;
}
.nip-pr-score {
  margin-left: auto;
  font-size: 12px;
  font-weight: 800;
  color: #334155;
  font-variant-numeric: tabular-nums;
}
.nip-pr-move {
  font-size: 11px;
  font-weight: 800;
  width: 34px;
  text-align: right;
  flex-shrink: 0;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}
.nip-pr-move--up { color: #334155; }
.nip-pr-move--down { color: #64748b; }
.nip-pr-move--new { color: #7c2d12; }
.nip-pr-cta {
  align-self: flex-start;
  margin-top: 2px;
  font-size: 11.5px;
  font-weight: 800;
  color: #7c2d12;
  text-decoration: none;
}
.nip-pr-cta:hover { text-decoration: underline; }
.nip-pr-cta:focus-visible { outline: 2px solid #9a3412; outline-offset: 2px; }

.nip-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
